/**
 * NobleAIBranchAnalyticsTile — Iter-102 · Item 6.
 *
 * Read-only CEO board tile that surfaces which of the 5 Noble AI visitor
 * branches (Prospective Client · Property Management · Tenants/Residents
 * · Field Staff · Other) generates the most live CS escalations per week.
 * Sourced from GET /api/support-chat/admin/branch-analytics (already live
 * in support_chat.py). Compute happens at the CS Department layer per
 * hierarchy rule — the CEO tile is display-only.
 */
import { useEffect, useState } from 'react';
import { apiSafeFetch } from '../../lib/apiFetch';
import { Card } from '../ui/card';
import { Bot, TrendingUp } from 'lucide-react';

const BRANCH_META = {
  prospect:         { label: 'Prospective Client',      color: '#22d3ee' },
  property_manager: { label: 'Property Management',     color: '#a78bfa' },
  resident:         { label: 'Tenants / Residents',     color: '#34d399' },
  field_staff:      { label: 'Field Staff',             color: '#fbbf24' },
  other:            { label: 'Other',                   color: '#f472b6' },
};

const BRANCH_ORDER = ['prospect', 'property_manager', 'resident', 'field_staff', 'other'];

export default function NobleAIBranchAnalyticsTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    apiSafeFetch('/api/support-chat/admin/branch-analytics?weeks=4').then(({ data: d }) => {
      if (!alive) return;
      setData(d);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  if (loading) return null;
  if (!data) return null;

  const totals = data.totals || {};
  const totalEscalations = BRANCH_ORDER.reduce((s, k) => s + (totals[k] || 0), 0);
  const topBranch = data.top_branch;
  const topMeta = topBranch ? BRANCH_META[topBranch] : null;

  return (
    <section data-testid="ceo-noble-ai-branch-tile">
      <h2 className="text-xs uppercase tracking-widest text-[#FFD60A] mb-3 font-semibold">
        Noble AI · Weekly Escalations by Branch
      </h2>
      <Card className="p-5 bg-zinc-900 border-zinc-800 text-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-[#FFD60A]" />
            <div>
              <p className="text-sm font-bold">Visitor Branch → CS Escalations</p>
              <p className="text-[11px] text-zinc-500">
                Last 4 weeks · Read-only. CS Department owns the underlying live data.
              </p>
            </div>
          </div>
          {topMeta && (
            <span
              className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest font-black px-3 py-1 rounded-full"
              style={{ backgroundColor: `${topMeta.color}22`, color: topMeta.color, border: `1px solid ${topMeta.color}` }}
              data-testid="ceo-noble-ai-top-branch"
              data-top-branch={topBranch}
            >
              <TrendingUp className="h-3.5 w-3.5" /> Top · {topMeta.label}
            </span>
          )}
        </div>

        <div className="grid grid-cols-5 gap-2">
          {BRANCH_ORDER.map((k) => {
            const meta = BRANCH_META[k];
            const count = totals[k] || 0;
            const pct = totalEscalations > 0 ? Math.round((count / totalEscalations) * 100) : 0;
            const isTop = k === topBranch && count > 0;
            return (
              <div
                key={k}
                className="rounded-lg p-3"
                style={{
                  backgroundColor: `${meta.color}${isTop ? '22' : '11'}`,
                  border: `1px solid ${isTop ? meta.color : `${meta.color}44`}`,
                }}
                data-testid={`ceo-noble-ai-branch-${k}`}
                data-count={count}
              >
                <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: meta.color }}>
                  {meta.label}
                </p>
                <p className="text-2xl font-black text-white leading-tight">{count}</p>
                <p className="text-[10px] text-zinc-400">{pct}% · 4wk</p>
              </div>
            );
          })}
        </div>

        {totalEscalations === 0 && (
          <p className="text-[11px] text-zinc-500 text-center mt-3" data-testid="ceo-noble-ai-empty">
            No live escalations yet in the last 4 weeks.
          </p>
        )}
      </Card>
    </section>
  );
}
